import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'motion/react'
import { X, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { postsApi, accountsApi } from '@/api'
import { useToast } from './Toast'

type ReportReason = 'spam' | 'harassment' | 'misinformation' | 'inappropriate' | 'other'

const REASON_KEYS: Array<{ value: ReportReason; key: string }> = [
  { value: 'spam', key: 'report.reason_spam' },
  { value: 'harassment', key: 'report.reason_harassment' },
  { value: 'misinformation', key: 'report.reason_misinformation' },
  { value: 'inappropriate', key: 'report.reason_inappropriate' },
  { value: 'other', key: 'report.reason_other' },
]

interface ReportModalProps {
  open: boolean
  onClose: () => void
  postId?: string
  username?: string
}

export function ReportModal({ open, onClose, postId, username }: ReportModalProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [reason, setReason] = useState<ReportReason>('spam')
  const [details, setDetails] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (!open) {
      setReason('spam')
      setDetails('')
      setSuccess(false)
    }
  }, [open])

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        onClose()
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [success, onClose])

  const handleSubmit = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    const finalReason = reason === 'other' && details.trim() ? `other: ${details.trim()}` : reason
    try {
      if (postId) {
        await postsApi.report(postId, finalReason)
      } else if (username) {
        await accountsApi.report(username, finalReason)
      }
      setSuccess(true)
    } catch {
      toast.error('Could not submit report. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 200,
              background: 'rgba(8,10,15,0.52)',
              backdropFilter: 'blur(2px)',
            }}
          />

          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            style={{
              position: 'fixed',
              insetInlineStart: 0,
              insetInlineEnd: 0,
              bottom: 0,
              zIndex: 201,
              background: 'var(--surface)',
              borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              boxShadow: 'var(--shadow-modal)',
              maxHeight: '90dvh',
              display: 'flex',
              flexDirection: 'column',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            {/* Handle bar */}
            <div
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                background: 'var(--border-strong)',
                margin: '10px auto 0',
                flexShrink: 0,
              }}
            />

            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px 0',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 700,
                  fontSize: 'var(--fs-body)',
                  color: 'var(--text-strong)',
                  flex: 1,
                }}
              >
                {t('report.title')}
              </span>
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 4,
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 0' }}>
              {success ? (
                <div
                  style={{
                    padding: '24px 0',
                    textAlign: 'center',
                    fontFamily: 'var(--font-sans)',
                    color: 'var(--text-body)',
                    fontSize: 'var(--fs-body)',
                  }}
                >
                  {t('report.success')}
                </div>
              ) : (
                <>
                  {REASON_KEYS.map(({ value, key }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setReason(value)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 0',
                        border: 'none',
                        borderBottom: '1px solid var(--border)',
                        background: 'none',
                        cursor: 'pointer',
                        textAlign: 'start',
                      }}
                    >
                      {/* Radio circle */}
                      <div
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          border: `2px solid ${reason === value ? 'var(--brand)' : 'var(--border-strong)'}`,
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {reason === value && (
                          <div
                            style={{
                              width: 9,
                              height: 9,
                              borderRadius: '50%',
                              background: 'var(--brand)',
                            }}
                          />
                        )}
                      </div>
                      <span
                        style={{
                          fontFamily: 'var(--font-sans)',
                          fontSize: 'var(--fs-body)',
                          color: 'var(--text-body)',
                        }}
                      >
                        {t(key)}
                      </span>
                    </button>
                  ))}

                  {reason === 'other' && (
                    <div style={{ marginTop: 12 }}>
                      <textarea
                        value={details}
                        onChange={(e) => setDetails(e.target.value.slice(0, 200))}
                        placeholder={t('report.describe_placeholder')}
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: 'var(--radius-md)',
                          border: '1.5px solid var(--border)',
                          background: 'var(--bg-sunk)',
                          color: 'var(--text-strong)',
                          fontFamily: 'var(--font-sans)',
                          fontSize: 'var(--fs-sm)',
                          lineHeight: 'var(--lh-normal)',
                          boxSizing: 'border-box',
                          resize: 'vertical',
                          outline: 'none',
                        }}
                      />
                      <div
                        style={{
                          textAlign: 'end',
                          fontSize: 'var(--fs-xs)',
                          fontFamily: 'var(--font-sans)',
                          color: 'var(--text-faint)',
                          marginTop: 4,
                        }}
                      >
                        {details.length}/200
                      </div>
                    </div>
                  )}
                  <div style={{ height: 16 }} />
                </>
              )}
            </div>

            {/* Footer */}
            {!success && (
              <div
                style={{
                  padding: '12px 16px',
                  borderTop: '1px solid var(--border)',
                  flexShrink: 0,
                }}
              >
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={handleSubmit}
                  style={{
                    width: '100%',
                    padding: '11px 0',
                    borderRadius: 'var(--radius-md)',
                    border: 'none',
                    background: 'var(--danger)',
                    color: '#fff',
                    fontFamily: 'var(--font-sans)',
                    fontWeight: 700,
                    fontSize: 'var(--fs-body)',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    opacity: isSubmitting ? 0.7 : 1,
                    transition: 'opacity var(--dur-fast)',
                  }}
                >
                  {isSubmitting && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
                  {t('report.submit')}
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  )
}
